import Sources from '../../collections/Sources';
import Source from '../../models/Source';
import { baseApi } from 'core/api';
import { useSubscriptionsStore, useToastStore } from 'store/modules';

const state = () => ({
    _all: [],
    _loading: false,
    _error: false,
    _plans: null,
    _plansLoading: true,
    _rechargeOptions: [],
    _rechargeOptionsLoading: false,
    _billingAddress: null,
});

const getters = {
    billingAddress: (state) => state._billingAddress,
    billingSources: (state) => state._all,
    billingSourcesSimple() {
        return this.billingSources.filter((item) => !item.isTypeAch);
    },
    billingSourcesAch() {
        return this.billingSources.filter((item) => item.isTypeAch);
    },
    billingSourcesLoading: (state) => state._loading,
    billingSourcesError: (state) => state._error,
    billingPlans: (state) => state._plans,
    billingPlansLoading: (state) => state._plansLoading,
    rechargeOptions: (state) => state._rechargeOptions,
    rechargeOptionsLoading: (state) => state._rechargeOptionsLoading,
};

const actions = {
    getBillingSources({ useLoading = true } = {}) {
        if (this._loading) return;

        if (useLoading) {
            this._setBillingSourcesLoading(true);
        }

        return baseApi
            .get('core/billing/sources')
            .then((response) => {
                const sources = new Sources(response.data).models;

                this._setBillingSourcesLoaded(sources);

                return sources;
            })
            .catch((error) => {
                this._setBillingSourcesLoadError();
                return error;
            })
            .finally(() => {
                if (useLoading) {
                    this._setBillingSourcesLoading(false);
                }
            });
    },

    loadingBillingSource(status = false) {
        this._setBillingSourcesLoading(status);
    },

    createBillingSources(form) {
        return form
            .put('core/billing/sources')
            .then((response) => {
                const source = new Source(response.data);

                this._setBillingSourceCreated(response.data);

                return source;
            })
            .catch((error) => {
                throw {
                    error: error.response.data,
                };
            });
    },

    getBillingAddress() {
        return baseApi
            .get('billing/billing_address')
            .then((response) => {
                this._setBillingAddress(response.data);

                return response.data;
            })
            .catch((error) => {
                throw {
                    error: error.response.data,
                };
            });
    },

    _setBillingAddress(data) {
        this._billingAddress = data;
    },

    updateBillingAddress(form) {
        return form
            .post('billing/billing_address')
            .then((response) => {
                this._setBillingAddressUpdated(response.data);

                return response.data;
            })
            .catch((error) => {
                throw {
                    error: error.response.data,
                };
            });
    },

    switchBillingAddress(data) {
        return baseApi
            .post('billing/billing_address', data)
            .then((response) => {
                this._setBillingAddressUpdated(response.data);

                return response.data;
            })
            .catch((error) => {
                throw {
                    error: error.response.data,
                };
            });
    },

    updateBillingSourcesById({ id, form }) {
        return form.post(`core/billing/sources/${id}`).then((response) => {
            const source = new Source(response.data);

            this._setBillingSourceUpdated(source);

            return source;
        });
    },

    deleteBillingSourcesById(form) {
        return form.delete(`core/billing/sources/${form.source}`).then((response) => {
            this._setBillingSourceDeleted(form.source);
            return response;
        });
    },

    getBillingCouponsById(c) {
        return baseApi.get(`core/billing/coupons/${c}`).then((response) => {
            return response.data;
        });
    },

    applyCoupon(c) {
        return baseApi.post(`core/billing/coupons/${c}`).then((response) => {
            return response.data;
        });
    },

    updateBillingUpgradeResetCycle() {
        return baseApi.put('core/billing/upgrade/reset_cycle').then((response) => {
            return response.data;
        });
    },

    getBillingPayments({ more, nextPage }) {
        const url = more
            ? `core/billing/payments?page=${nextPage}`
            : 'core/billing/payments?page=1';

        return baseApi.get(url).then((response) => {
            return response.data;
        });
    },

    updateBillingUpgradePlan({ params, form }) {
        return baseApi.post('core/billing/upgrade/plan', params).then((response) => {
            useSubscriptionsStore().subscriptionUpgraded(response.data);

            return response.data;
        });
    },

    updateBillingPaymentsRefreshById({ form, id }) {
        return form.put(`core/billing/payments/refresh/${id}`).then((response) => {
            return response.data;
        });
    },

    updateBillingRestart({ form }) {
        return form.put('core/billing/restart').then((response) => {
            return response.data;
        });
    },

    getBillingStatesMicroservice() {
        return baseApi.get('billing/states').then((response) => {
            return response.data.data;
        });
    },

    getBillingAreaCodesMicroservice() {
        return baseApi.get('billing/area-codes').then((response) => {
            return response.data.data;
        });
    },

    getBillingPlansMicroservice() {
        this._setBillingPlansLoading(true);

        return baseApi
            .get('billing/plans')
            .then((response) => {
                this._setBillingPlans(response.data);
                return response.data;
            })
            .finally(() => {
                this._setBillingPlansLoading(false);
            });
    },

    createBillingSetupIntentMicroservice() {
        return baseApi.post('billing/setup-intent').then((response) => {
            return response.data.secret;
        });
    },

    updateBillingRestartMicroservice({ params }) {
        return baseApi.post('billing/restart', params).then((response) => {
            return response.data;
        });
    },

    updateBillingUpgradePlanMicroservice({ params }) {
        return baseApi
            .post('billing/upgrade/plan', params)
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                useToastStore().showToast({
                    type: 'danger',
                    message: error.response?.data?.error,
                });
                throw {
                    error: error.response.data,
                };
            });
    },

    updateBillingPayAsYouGoMicroservice() {
        return baseApi.post('billing/pay-as-you-go').then((response) => {
            return response.data;
        });
    },

    updateBillingSeatsMicroservice({ params }) {
        return baseApi.post('billing/seats', params).then((response) => {
            return response;
        });
    },

    deleteBillingSeatsMicroservice(params) {
        return baseApi
            .delete('billing/seats', params)
            .then((response) => {
                return response;
            })
            .catch((e) => e);
    },

    createBillingNumbersMicroservice({ params }) {
        return baseApi.post('billing/numbers', params).then((response) => {
            return response;
        });
    },

    deleteBillingNumbersMicroservice(params) {
        return baseApi
            .delete('billing/numbers', params)
            .then((response) => {
                return response;
            })
            .catch((e) => e);
    },

    getBillingPreviewSeatsMicroservice(params) {
        return baseApi.get('billing/preview/seats', { params }).then((response) => {
            return response.data;
        });
    },

    getBillingPreviewSharedInboxesMicroservice(params) {
        return baseApi.get('billing/preview/shared-inboxes', params).then((response) => {
            return response.data;
        });
    },

    getBillingChurnkeyAuthMicroservice() {
        return baseApi.get('billing/churnkey/auth').then((response) => {
            return response.data;
        });
    },

    updateBillingDepositReplenishMicroservice(params) {
        return baseApi.post('billing/deposit-replenish', params).then((response) => {
            const data = response.data;

            this._setDlcUpdateState(['profileDepositBalance', data.deposit_balance]);

            return response.data;
        });
    },

    updateStateBillingSources(data) {
        this._setBillingSourcesUpdateState(data);
    },

    getRechargeOptions() {
        if (this.rechargeOptionsLoading || this.rechargeOptions.length > 0) {
            return this.rechargeOptions;
        }

        this._setRechargeOptionsLoading();

        return baseApi.get('billing/additional-credit-price').then((response) => {
            this._setRechargeOptionsLoaded(response.data);

            return response.data;
        });
    },

    updateBillingCustomDomain() {
        return baseApi
            .post('core/billing/custom-domain/subscribe')
            .then((response) => {
                return response.data;
            })
            .catch((e) => {
                return e.response.data;
            });
    },

    getBillingCustomDomain() {
        return baseApi.get('core/billing/custom-domain/info').then((response) => {
            return response.data;
        });
    },

    // Private methods (converted from mutations)
    _setBillingSourcesUpdateState([key, value]) {
        this[`_${key}`] = value;
    },

    _setBillingSourcesLoading(status) {
        this._error = false;
        this._loading = status;
    },

    _setBillingSourcesLoaded(sources) {
        this._all = sources;
    },

    _setBillingSourcesLoadError() {
        this._all = [];
        this._error = true;
    },

    _setBillingSourceCreated(source) {
        if (source.primary) {
            this._all = this._all.map((o) => {
                o.primary = false;
                return o;
            });
        }

        this._all.push(source);
    },

    _setBillingAddressLoaded(source) {
        this._billingAddress = source;
    },

    _setBillingAddressUpdated(source) {
        if (source?.use_business_address) {
            this._billingAddress = {
                ...this._billingAddress,
                '10dlc_address': source,
                use_business_address: source.use_business_address,
            };
        } else {
            this._billingAddress = {
                ...this._billingAddress,
                billing_address: source,
                use_business_address: source.use_business_address,
            };
        }
    },

    _setBillingSourceUpdated(source) {
        this._all = this._all.map((o) => {
            if (o.id !== source.id) {
                o.primary = false;
                return o;
            }

            return source;
        });
    },

    _setBillingSourceDeleted(source) {
        this._all = this._all.filter((o) => o.id !== source);
    },

    _setBillingPlans(source) {
        this._plans = source;
    },

    _setBillingPlansLoading(status) {
        this._plansLoading = status;
    },

    _setRechargeOptionsLoading() {
        this._rechargeOptionsLoading = true;
    },

    _setRechargeOptionsLoaded(plans) {
        this._rechargeOptions = plans;
        this._rechargeOptionsLoading = false;
    },

    _setDlcUpdateState([key, value]) {
        this[`_${key}`] = value;
    },
};

export default {
    state,
    getters,
    actions,
};
